import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import React, { Fragment, lazy, useState } from 'react';
import { PortalConsumer } from '../../../context';
import { FormStateChangeNotifier } from '../form';
import { useIsInForm } from '../utils/useIsInForm';
import { Label } from '../label';
import { FieldContainer } from '../utils/FieldContainer';
var AKFormField = /*#__PURE__*/lazy(function () {
  return import( /* webpackChunkName: '@atlaskit-internal_.form' */
  '@atlaskit/form/Field');
});
var AKSelect = /*#__PURE__*/lazy(function () {
  return import( /* webpackChunkName: '@atlaskit-internal_.select' */
  '@atlaskit/select');
});
var AKErrorMessage = /*#__PURE__*/lazy(function () {
  return import( /* webpackChunkName: '@atlaskit-internal_.form' */
  '@atlaskit/form').then(function (module) {
    return {
      default: module.ErrorMessage
    };
  });
});
var AKHelperMessage = /*#__PURE__*/lazy(function () {
  return import( /* webpackChunkName: '@atlaskit-internal_.form' */
  '@atlaskit/form').then(function (module) {
    return {
      default: module.HelperMessage
    };
  });
});
var validate = function validate(value) {
  return !value ? 'EMPTY' : undefined;
};
var FormSelect = function FormSelect(_ref) {
  var options = _ref.options,
    isMulti = _ref.isMulti,
    defaultValue = _ref.defaultValue,
    label = _ref.label,
    name = _ref.name,
    description = _ref.description,
    isRequired = _ref.isRequired,
    placeholder = _ref.placeholder,
    _onChange = _ref.onChange;
  return /*#__PURE__*/React.createElement(PortalConsumer, null, function (portal) {
    return /*#__PURE__*/React.createElement(FieldContainer, null, /*#__PURE__*/React.createElement(AKFormField, {
      defaultValue: defaultValue,
      label: label,
      name: name,
      isRequired: isRequired,
      validate: isRequired ? validate : undefined
    }, function (_ref2) {
      var fieldProps = _ref2.fieldProps,
        error = _ref2.error;
      var _ref3 = fieldProps,
        id = _ref3.id,
        isDisabled = _ref3.isDisabled,
        isRequired = _ref3.isRequired,
        isInvalid = _ref3.isInvalid,
        onBlur = _ref3.onBlur,
        onFocus = _ref3.onFocus,
        value = _ref3.value,
        ariaInvalid = _ref3['aria-invalid'],
        ariaLabelledBy = _ref3['aria-labelledby'];
      return /*#__PURE__*/React.createElement(Fragment, null, /*#__PURE__*/React.createElement(FormStateChangeNotifier, {
        name: name,
        value: fieldProps.value
      }), /*#__PURE__*/React.createElement(AKSelect, {
        id: id,
        isDisabled: isDisabled,
        isRequired: isRequired,
        isInvalid: isInvalid,
        onBlur: onBlur,
        onFocus: onFocus,
        value: value,
        name: fieldProps.name,
        onChange: function onChange(option) {
          fieldProps.onChange(option);
          _onChange === null || _onChange === void 0 || _onChange(option);
        },
        isMulti: isMulti,
        menuPortalTarget: portal,
        options: options,
        placeholder: placeholder,
        validationState: error ? 'error' : 'default',
        "aria-invalid": ariaInvalid,
        "aria-labelledby": ariaLabelledBy
      }), error === 'EMPTY' && /*#__PURE__*/React.createElement(AKErrorMessage, null, "This field is required."), description && /*#__PURE__*/React.createElement(AKHelperMessage, null, description));
    }));
  });
};
var SelectInner = function SelectInner(_ref4) {
  var options = _ref4.options,
    isMulti = _ref4.isMulti,
    label = _ref4.label,
    placeholder = _ref4.placeholder,
    _onChange2 = _ref4.onChange,
    defaultValue = _ref4.defaultValue,
    description = _ref4.description,
    name = _ref4.name,
    isRequired = _ref4.isRequired,
    children = _ref4.children;
  var _useState = useState(defaultValue || null),
    _useState2 = _slicedToArray(_useState, 2),
    value = _useState2[0],
    setValue = _useState2[1];
  return /*#__PURE__*/React.createElement(FieldContainer, null, /*#__PURE__*/React.createElement(Label, {
    name: name,
    isRequired: isRequired,
    label: label
  }), /*#__PURE__*/React.createElement(AKSelect, {
    isMulti: isMulti,
    value: value,
    onChange: function onChange(option) {
      setValue(option);
      _onChange2 === null || _onChange2 === void 0 || _onChange2(option);
    },
    options: options,
    placeholder: placeholder,
    description: description,
    name: name,
    isRequired: isRequired
  }, children), description && /*#__PURE__*/React.createElement(AKHelperMessage, null, description));
};
export var Select = function Select(props) {
  var _ref5 = props.forgeDoc.props,
    description = _ref5.description,
    label = _ref5.label,
    name = _ref5.name,
    isMulti = _ref5.isMulti,
    placeholder = _ref5.placeholder,
    isRequired = _ref5.isRequired,
    onChange = _ref5.onChange;
  var defaultValue = [];
  var options = props.forgeDoc.children.map(function (child) {
    var props = child.props,
      type = child.type;
    if (type === 'Option') {
      var _ref6 = props,
        defaultSelected = _ref6.defaultSelected,
        _label = _ref6.label,
        value = _ref6.value;
      var option = {
        label: _label,
        value: value
      };
      if (defaultSelected) {
        if (isMulti) {
          defaultValue.push(option);
        } else {
          defaultValue = [option];
        }
      }
      return option;
    }
  }).filter(function (option) {
    return typeof option !== 'undefined';
  });
  var formattedDefaultValue = isMulti ? defaultValue : defaultValue[0];
  var isInForm = useIsInForm();
  if (!isInForm) {
    return /*#__PURE__*/React.createElement(SelectInner, {
      label: label,
      name: name,
      options: options,
      isMulti: isMulti,
      isRequired: isRequired,
      defaultValue: formattedDefaultValue,
      description: description,
      placeholder: placeholder,
      onChange: onChange
    });
  }
  return /*#__PURE__*/React.createElement(FormSelect, {
    label: label,
    name: name,
    options: options,
    isMulti: isMulti,
    isRequired: isRequired,
    defaultValue: formattedDefaultValue,
    description: description,
    placeholder: placeholder,
    onChange: onChange
  });
};